import { DataGrid, GridColDef, GridToolbar } from "@mui/x-data-grid";
import {
  ProgramReport,
  roundToTwoDecimals,
  useDynamicProgramReport
} from "./reportUtils";
import { Alert, CircularProgress } from "@mui/material";
import { DataFreshness } from "./DataFreshness";
import { ReportLevel, ReportSelectionPrompt } from "./ReportSelectionPrompt";

interface DynamicProgramReportProps {
  institutionId?: string;
  startDate?: string;
  endDate?: string;
  columns: GridColDef<ProgramReport>[];
}

export const DynamicProgramReport = ({
  institutionId,
  startDate,
  endDate,
  columns
}: DynamicProgramReportProps) => {
  const {
    report,
    status: fetchReportStatus,
    error: fetchReportError
  } = useDynamicProgramReport(institutionId, startDate, endDate);

  if (!institutionId) {
    return <ReportSelectionPrompt reportLevel={ReportLevel.Institution} />;
  }

  if (fetchReportStatus === "idle") {
    return <div>Awaiting Data...</div>;
  }

  if (fetchReportStatus === "loading") {
    return <CircularProgress />;
  }

  if (fetchReportStatus === "failed") {
    return <Alert severity="error">{fetchReportError}</Alert>;
  }

  const formattedReport: ProgramReport[] = report.map((row) => ({
    ...row,
    id: "",
    programId: row.programId ?? "",
    programName: row.program ?? "",
    institutionId: row.institutionId ?? "",
    institutionName: row.institutionName ?? "",
    completedAssessmentEvents: row.completedAssessmentEvents ?? 0,
    createdAssessmentEvents: row.createdAssessmentEvents ?? 0,
    assignedAssessmentEvents: row.assignedAssessmentEvents ?? 0,
    attendingsInProgram: row.attendingsInProgram ?? 0,
    traineesInProgram: row.attendingsInProgram ?? 0,
    percentCompleted: row.percentCompleted
      ? roundToTwoDecimals(row.percentCompleted * 100)
      : 0,
    averageCompletedPerTrainee: row.averageCompletedPerTrainee
      ? roundToTwoDecimals(row.averageCompletedPerTrainee)
      : 0
  }));

  return (
    <>
      <DataFreshness />
      <DataGrid
        aria-label="reports-table"
        rows={formattedReport}
        columns={columns}
        getRowId={(row) => row.programId!}
        slots={{ toolbar: GridToolbar }}
      />
    </>
  );
};
